/**
 * The campaigns screen's rules, where a runner can reach them.
 *
 * Out of `Campaigns.tsx` for the reason `overview-copy.ts` gives: `node --test`
 * loads no `.tsx`, and `api.ts` reads `import.meta.env`. Only types come from
 * `api.ts` here, and those are erased before anything runs.
 *
 * Every rule is docs/API-CONTRACT.md's, read from the client's side: what the
 * worker would refuse with a 400 is refused here first, in the worker's order and
 * with its words (`routes/v1.ts`, `campaignFromBody`); what it answers is sorted by
 * structure — `created`, the status — never by reading a sentence back.
 *
 * The same file lives in ttads, and the platform's fields are what differ, with one
 * exception as of 2026-09-15: `canWriteAccount` here checks in the kit's own order
 * (platform admin, then the active team, then grants) and takes `Pick<Standing>`,
 * where ttads's branches on `standing.standing`. The kit's order is e1's decision for
 * both consoles; `mayCreateAgain`, `outcomeAfterReload` and the outcome kinds match.
 */
import type { Grant, Standing } from "./api.ts";

/**
 * Refused on presence by `POST /api/v1/campaigns` (400). The form has no field for
 * any of them and the request is built field by field, so none can be sent.
 * `budgetOptimization` left the list on 2026-10-23: LINE_ITEM is carried when the
 * budget is on each ad group (`CampaignDraft.budgetOptimization`), and nothing else.
 */
export const REFUSED_ON_PRESENCE = [
	"entityStatus",
	"status",
	"standardDelivery",
	"purchaseOrderNumber",
] as const;

/** The form as typed. */
export interface CampaignDraft {
	name: string;
	fundingInstrumentId: string;
	dailyBudgetAmountLocalMicro: string;
	totalBudgetAmountLocalMicro: string;
	/** LINE_ITEM when the budget is on each ad group; left out otherwise. */
	budgetOptimization?: "LINE_ITEM";
}

/** `POST /api/v1/campaigns`' body (v1), and nothing else. */
export interface CampaignCreate {
	/** X's ad account id. */
	account: string;
	name: string;
	fundingInstrumentId: string;
	dailyBudgetAmountLocalMicro?: number;
	totalBudgetAmountLocalMicro?: number;
	/** Sent only as LINE_ITEM, when the budget is on each ad group (2026-10-23); absent, X sets its own. */
	budgetOptimization?: "LINE_ITEM";
}

/** `POST /api/v1/campaigns/:id/status`' body. */
export interface CampaignSwitch {
	account: string;
	status: TargetStatus;
}

export type TargetStatus = "ACTIVE" | "PAUSED";

const BUDGETS = ["dailyBudgetAmountLocalMicro", "totalBudgetAmountLocalMicro"] as const;

/** The worker's words for a budget it refuses. */
export const budgetError = (key: (typeof BUDGETS)[number]) =>
	`${key} must be a whole, non-negative number of micro units of the funding instrument's currency`;

/**
 * The request, or the 400 the worker would answer — checked in the worker's order.
 * A budget left empty is absent (neither is required; X arbitrates); otherwise it is
 * whole micro units, 0 or more, passed on as typed. `name` is sent as typed; its
 * length is X's to refuse.
 */
export function campaignRequest(
	account: string,
	draft: CampaignDraft,
): { ok: true; value: CampaignCreate } | { ok: false; error: string } {
	if (!account) return { ok: false, error: "account is required" };
	if (!draft.name.trim()) return { ok: false, error: "name is required" };
	if (!draft.fundingInstrumentId) return { ok: false, error: "fundingInstrumentId is required" };

	const value: CampaignCreate = {
		account,
		name: draft.name,
		fundingInstrumentId: draft.fundingInstrumentId,
	};
	for (const key of BUDGETS) {
		const text = draft[key].trim();
		if (text === "") continue;
		const n = Number(text);
		if (!/^\d+$/.test(text) || !Number.isSafeInteger(n))
			return { ok: false, error: budgetError(key) };
		value[key] = n;
	}
	if (draft.budgetOptimization === "LINE_ITEM") value.budgetOptimization = "LINE_ITEM";
	return { ok: true, value };
}

/** What a read-back says about a campaign. */
export interface CampaignState {
	id: string;
	entityStatus: string;
	servable: boolean;
	reasonsNotServable: string[];
}

/**
 * How a create ended.
 *
 *   created   201, from the read-back
 *   exists    `created: true` — the object exists; a retry makes a second one
 *   unknown   `created: "unknown"`, or no answer to read — it may exist
 *   refused   anything else — nothing was made
 */
export type CreateOutcome =
	| ({ kind: "created" } & CampaignState)
	| { kind: "exists"; id: string | null; error: string }
	| { kind: "unknown"; error: string }
	| { kind: "refused"; status: number; error: string };

const record = (body: unknown): Record<string, unknown> =>
	typeof body === "object" && body !== null && !Array.isArray(body)
		? (body as Record<string, unknown>)
		: {};

const errorOf = (b: Record<string, unknown>, status: number): string =>
	typeof b.error === "string" ? b.error : `HTTP ${status}`;

const strings = (value: unknown): string[] =>
	Array.isArray(value) ? value.filter((r): r is string => typeof r === "string") : [];

export function createOutcome(status: number, body: unknown): CreateOutcome {
	const b = record(body);
	if (b.created === true) {
		return {
			kind: "exists",
			id: typeof b.id === "string" ? b.id : null,
			error: errorOf(b, status),
		};
	}
	if (b.created === "unknown") return { kind: "unknown", error: errorOf(b, status) };
	if (status === 201) {
		// Made, whatever else the body lacks: without an id there is nothing to call created.
		if (typeof b.id !== "string") {
			return { kind: "exists", id: null, error: "created, but the answer did not name it" };
		}
		return {
			kind: "created",
			id: b.id,
			entityStatus: typeof b.entityStatus === "string" ? b.entityStatus : "",
			servable: b.servable === true,
			reasonsNotServable: strings(b.reasonsNotServable),
		};
	}
	return { kind: "refused", status, error: errorOf(b, status) };
}

/**
 * The create threw instead of answering: the browser's fetch failed, or the answer
 * was not JSON. The request may have reached X, so this is not a refusal.
 */
export function createThrew(message: string): CreateOutcome {
	return { kind: "unknown", error: message };
}

/**
 * Whether Create may be pressed again after this answer. Not after `exists` or
 * `unknown`: the draft is still filled in, and one more click would make a second
 * campaign. The screen keeps Create locked until the list has been read again.
 */
export function mayCreateAgain<T extends Locking>(outcome: T): boolean {
	return outcome.kind === "created" || outcome.kind === "refused";
}

/**
 * The four ways a create can end, and nothing about what it created.
 *
 * 🔴 The lock turns on these four words and on an id — never on the payload — which
 * is why the line-item screen can use the same two functions rather than copy them.
 * A copy is a second place to fix, and the copy is the one that would not be fixed
 * (twads#87 took two rounds in ONE screen).
 */
export type Locking = { kind: CreateOutcome["kind"]; id?: string | null };

/**
 * What `GET /campaigns/:id` established: `found`, or `absent` — X answered and does
 * not have it — or `unasked`, which covers both "there was no id to ask with" and
 * "the read did not come back".
 *
 * Whether the *list* answered is not here, because nothing below reads it any more.
 * It was the input this function used to decide on, and deciding on it is the bug
 * (twads#87); a field kept for old times' sake would be the next thing someone
 * reaches for.
 */
export type FoundById = "found" | "absent" | "unasked";

/**
 * The answer still shown after "Reload list".
 *
 * 🔴 **A successful list no longer lifts anything.** X's campaign list lags behind
 * a create — measured 2026-09-29: 0 items a minute after a create that had already
 * answered 200 by id, 1 item three minutes later (twads#86, docs/API-CONTRACT.md).
 * A list that answered therefore says nothing about a campaign created seconds ago,
 * and lifting the lock on it told the operator that a campaign which exists does
 * not — draft still filled in, Create enabled, one click from a second campaign
 * with a budget (twads#87).
 *
 * What lifts it is the one read that does not lag, and only where there is
 * something to read with:
 *
 *   - `exists` carries an id (`v1.ts:744`) → `GET /campaigns/:id` decides.
 *   - `unknown` carries none (`v1.ts:721`) → **nothing here can lift it.** Reading
 *     by id is not an option in principle, not merely unimplemented: there is no
 *     id. The way out is the deliberate action, not a reload that came back 200.
 *
 * The asymmetry is the uncomfortable part and belongs in the open: the read that
 * does not lag is available in the *less* dangerous case and absent in the more
 * dangerous one (d6, twads#87).
 */
export function outcomeAfterReload<T extends Locking>(
	outcome: T | null,
	byId: FoundById,
): T | null {
	if (outcome === null) return null;
	if (outcome.kind === "exists" && (outcome.id ?? null) !== null)
		return byId === "found" ? null : outcome;
	// Every other locking answer — `unknown`, or an `exists` whose answer named no id.
	return outcome;
}

/**
 * What a read of `GET /campaigns/:id` established — from the answer's own shape,
 * so a 404 and a failure are not the same word.
 *
 * `absent` is only for X answering that it does not have it. Anything else the
 * console might say — a 502 it could not read through, a 403, a body without the
 * id asked for — is `unasked`: nothing was established, and the lock stays.
 *
 * 🔴 **`absent` has no behaviour of its own, on purpose, and that is not an
 * oversight to tidy up later.** A 404 after an answer that said `created: true`
 * with an id is a contradiction between two things X told us, not the resolution
 * of one: the create named the campaign and the read denies it. Treating it as
 * "so it was not created" would be choosing which of X's answers to believe, on
 * the strength of a single observation that a read by id does not lag — one
 * measurement, 2026-09-29, from which the contract itself refuses to draw a bound.
 * So it lands where `unasked` lands, keeping the lock, and the operator goes to X.
 * It is kept as its own value because the day that contradiction is measured, this
 * is where the answer goes.
 */
export function foundById(status: number, body: unknown, id: string): FoundById {
	if (status === 404) return "absent";
	if (status !== 200) return "unasked";
	return record(body).id === id ? "found" : "unasked";
}

/**
 * The rows on screen after another page arrives — campaigns, line items, anything
 * X pages by cursor.
 *
 * Named `appendRows` and not `appendCampaigns` since the line-item screen became
 * its second caller: the signature was always generic (`T extends { id: string }`),
 * and a name that says "campaigns" would have sent the next reader looking for a
 * campaign-shaped rule that is not here.
 *
 * X's paging is by cursor over a collection that is being written to while it is
 * read, so the same campaign can appear on two pages — this keeps the first copy
 * and drops the later one. Without that, React sees two rows with one key, and a
 * list that looks longer than the account is.
 *
 * Order is arrival order: what X sent first stays first. Nothing here sorts — the
 * screen shows X's order, and `sort_by` is not sent.
 */
export function appendRows<T extends { id: string }>(shown: T[], arrived: T[]): T[] {
	const seen = new Set(shown.map((c) => c.id));
	const out = [...shown];
	for (const c of arrived) {
		if (seen.has(c.id)) continue;
		seen.add(c.id);
		out.push(c);
	}
	return out;
}

/**
 * What the pages held beside the first, and the cursor, become when a first page
 * arrives — a reload, another ad account, the list read again after a create.
 *
 * Always an empty list: rows loaded under one account must not survive into
 * another's list, and `appendRows` cannot save anyone from that, because
 * another account's rows have different ids and so are not duplicates of
 * anything. A page-two of the old account under page-one of the new is a screen
 * that lies about which account the operator is looking at.
 *
 * Out here rather than inside the effect so the rule can be read and tested
 * without rendering; `campaigns.test.ts` also pins the effect that calls it,
 * because a rule nothing calls is not a rule (d6, twads#67).
 */
export function resetToFirstPage<T>(page: { page: { nextCursor: string | null } } | null): {
	rows: T[];
	cursor: string | null;
} {
	return { rows: [], cursor: page?.page.nextCursor ?? null };
}

/** The one switch the contract offers. `DRAFT`, or anything else X reports, gets none. */
export function toggleTarget(entityStatus: string): TargetStatus | null {
	if (entityStatus === "ACTIVE") return "PAUSED";
	if (entityStatus === "PAUSED") return "ACTIVE";
	return null;
}

/**
 * How a status change ended.
 *
 *   set       200 and the read-back reports what was asked
 *   mismatch  the write was accepted and X reports otherwise: a 502 carrying the
 *             read-back's fields, `null` when it found no campaign
 *   refused   anything else — nothing changed
 */
export type StatusOutcome =
	| ({ kind: "set" } & CampaignState)
	| {
			kind: "mismatch";
			asked: TargetStatus;
			reported: string | null;
			servable: boolean | null;
			reasonsNotServable: string[] | null;
			error: string;
	  }
	| { kind: "refused"; status: number; error: string };

export function statusOutcome(asked: TargetStatus, status: number, body: unknown): StatusOutcome {
	const b = record(body);
	if (status === 200 && b.entityStatus === asked && typeof b.id === "string") {
		return {
			kind: "set",
			id: b.id,
			entityStatus: asked,
			servable: b.servable === true,
			reasonsNotServable: strings(b.reasonsNotServable),
		};
	}
	if ((status === 200 || status === 502) && "entityStatus" in b) {
		return {
			kind: "mismatch",
			asked,
			reported: typeof b.entityStatus === "string" ? b.entityStatus : null,
			servable: typeof b.servable === "boolean" ? b.servable : null,
			reasonsNotServable: Array.isArray(b.reasonsNotServable)
				? strings(b.reasonsNotServable)
				: null,
			error: errorOf(b, status),
		};
	}
	return { kind: "refused", status, error: errorOf(b, status) };
}

/**
 * The kit's `canWrite` for an ad account (`lib/principal.ts`), so the screen offers a
 * write only where the worker would allow one: a platform admin always; a team
 * member never; a seated `user` never; otherwise a `write` grant on the account, or
 * on every account — a team's from standing, a seated operator's from
 * `api.myAccess()`. This hides controls. It grants nothing.
 */
export function canWriteAccount(
	standing: Pick<Standing, "platform" | "team">,
	platformGrants: readonly Grant[],
	accountId: string,
): boolean {
	const role = standing.platform?.role;
	if (role === "admin" || role === "super_admin") return true;
	const tenant = standing.team?.status === "active" ? standing.team : null;
	if (tenant && tenant.role === "member") return false;
	if (!tenant && role === "user") return false;
	const grants = tenant ? tenant.grants : standing.platform ? platformGrants : [];
	return grants.some(
		(g) =>
			g.entityType === "x_accounts" &&
			(g.entityId === "*" || g.entityId === accountId) &&
			g.permission === "write",
	);
}
